import React, { useState, useMemo } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Pencil,
  Trash2,
  Upload,
  Loader2,
  Layers,
  Package,
  Truck,
  Copy,
  Calculator,
  Zap,
  CheckCircle2,
  Sparkles,
  Cpu,
  Boxes,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntityLink } from "@/components/shared/EntityLink";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Layers as LayersIcon, ListTree } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { trpcClient } from "@/lib/trpc";
import {
  formatMoney,
  formatDate,
  formatNumber,
  getUploadUrl,
  cleanBrandName,
  sanitizeReturnUrl,
  cn,
} from "@/lib/utils";
import { getStatusConfig } from "@/config/status-registry";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import DetailLoadingSkeleton from "@/components/shared/DetailLoadingSkeleton";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { InteractiveStatusSwitch } from "@/components/ui/interactive-status-switch";
import {
  ProductOrdersTab,
  ProductDeliveriesTab,
} from "./components/ProductTabs";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { VersionHistoryModal } from "./components/VersionHistoryModal";
import { ProductCostPreviewModal } from "./components/ProductCostPreviewModal";
import { ProductSyncResultModal } from "./components/ProductSyncResultModal";
import { ProcessGroupBadge } from "@/components/shared/ProcessGroupBadge";
import { formatBomNorm } from "@/shared/helpers/product-costing.helper";
import { DetailField as F } from "@/components/shared/DetailField";


const fmtD = (d: string | Date | null | undefined) => formatDate(d) || "—";

// ══════════════════════════════════════════════════════════════
export default function ProductView({
  productId: propId,
  isZIndex,
}: {
  productId?: string;
  isZIndex?: boolean;
}) {
  const params = useParams({ strict: false }) as any;
  const search = useSearch({ strict: false }) as { returnUrl?: string; tab?: string };
  const rawReturnUrl = search?.returnUrl;
  const returnUrl = rawReturnUrl ? sanitizeReturnUrl(rawReturnUrl, "") : undefined;
  const productId = propId || params.productId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState("bom");
  const { isAdmin, hasPermission } = useRoleAccess();
  const canCreate = isAdmin || hasPermission("products", "create");
  const canUpdate = isAdmin || hasPermission("products", "update");
  const canDelete = isAdmin || hasPermission("products", "delete");

  const handleBack = () => {
    if (returnUrl) {
      navigate({ to: returnUrl as any });
    } else if (window.history.length > 2) {
      window.history.back();
    } else {
      navigate({ to: "/products" });
    }
  };


  // Product findOne includes: customer, technicalSpecs, productType, material
  const { data: productData, isLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ["product", productId],
    queryFn: async () => trpcClient.products.getById.query({ id: productId }),
  });

  const product = productData as any;

  const linkedMolds = useMemo(() => {
    const list: any[] = [];
    const seen = new Set<string>();

    if (Array.isArray(product?.dieCutProducts)) {
      for (const dcp of product.dieCutProducts) {
        if (dcp.dieCut && !seen.has(dcp.dieCut.id)) {
          seen.add(dcp.dieCut.id);
          list.push({
            id: dcp.dieCut.id,
            code: dcp.dieCut.dieCutId || dcp.dieCut.id,
            name: dcp.dieCut.name,
            type: dcp.dieCut.type || "DIE_CUT",
            size: dcp.dieCut.size,
            ups: dcp.ups || dcp.dieCut.ups || 1,
            status: dcp.dieCut.status,
            notes: dcp.notes,
            isDirect: false,
          });
        }
      }
    }

    if (Array.isArray(product?.dieCuts)) {
      for (const dc of product.dieCuts) {
        if (!seen.has(dc.id)) {
          seen.add(dc.id);
          list.push({
            id: dc.id,
            code: dc.dieCutId || dc.id,
            name: dc.name,
            type: dc.type || "DIE_CUT",
            size: dc.size,
            ups: dc.ups || 1,
            status: dc.status,
            notes: dc.notes,
            isDirect: true,
          });
        }
      }
    }

    return list;
  }, [product]);

  const linkedPlates = useMemo(() => {
    return Array.isArray(product?.plates) ? product.plates : [];
  }, [product]);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [costPreviewOpen, setCostPreviewOpen] = useState(false);
  const [syncResult, setSyncResult] = useState<any | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSyncingMaster, setIsSyncingMaster] = useState(false);

  const { data: decompositionData, isLoading: isLoadingDecomposition, refetch: refetchDecomposition } = useQuery({
    queryKey: ["product-decomposition", productId],
    queryFn: async () => trpcClient.products.decomposeCost.query({ id: productId }),
    enabled: !!productId && !!product,
  });

  const handleSyncMaster = async () => {
    try {
      setIsSyncingMaster(true);
      const res = await trpcClient.products.syncDecomposedBOMAndRouting.mutate({
        id: productId,
      });
      setSyncResult(res);
      toast.success(res.message || "Chuẩn hóa và đồng bộ thành công vào Master Data!");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["product", productId] }),
        queryClient.invalidateQueries({ queryKey: ["products"] }),
        queryClient.invalidateQueries({ queryKey: ["products-lookup"] }),
        queryClient.invalidateQueries({ queryKey: ["entity-select", "products"] }),
        queryClient.invalidateQueries({
          predicate: (q) => JSON.stringify(q.queryKey).includes("combobox"),
        }),
        refetchDecomposition(),
      ]);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi đồng bộ vào Master Data");
    } finally {
      setIsSyncingMaster(false);
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await trpcClient.products.delete.mutate({ id: productId });
      await queryClient.invalidateQueries({ queryKey: ["products"] });
      await queryClient.invalidateQueries({ queryKey: ["activity-log"] });
      await queryClient.invalidateQueries({ queryKey: ["products-lookup"] });

      setIsDeleting(false);
      navigate({ to: "/products" });
    } catch (err: any) {
      setIsDeleting(false);
      toast.error(err?.message || "Xoá thất bại");
    }
  };

  if (isLoading) {
    return <DetailLoadingSkeleton breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]} />;
  }

  if (detailError || !product) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]}
        entityLabel="Sản phẩm"
        backTo="/products"
        codeOrId={productId}
        error={detailError}
        onRetry={() => refetchDetail()}
      />
    );
  }

  // ── Parse specs (Json field) ──
  let specsObj: any = {};
  try {
    if (typeof product.specs === "object" && product.specs)
      specsObj = product.specs;
    else if (typeof product.specs === "string" && product.specs.startsWith("{"))
      specsObj = JSON.parse(product.specs);
  } catch {
    /* */
  }

  // Kích thước: form stores as 'size' or 'legacy'
  const sizeValue = specsObj.size || specsObj.legacy || "";
  const printSizeValue = specsObj.printSize || "";
  const purchaseSizeValue = specsObj.purchaseSize || "";
  const outsValue = specsObj.outs || "";
  const hasSpecs = !!(
    sizeValue ||
    printSizeValue ||
    purchaseSizeValue ||
    outsValue
  );

  // Legacy support for old dai/rong/cao format
  const specsParts = [
    specsObj.dai ? `${specsObj.dai}` : "",
    specsObj.rong ? `${specsObj.rong}` : "",
    specsObj.cao ? `${specsObj.cao}` : "",
  ].filter(Boolean);

  // Process from junction table (N:N)
  const junctionProcesses: any[] = product.productProcesses || [];
  // Fallback: parse from text if junction is empty
  const textProcesses: string[] =
    !junctionProcesses.length && product.process
      ? product.process
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean)
      : [];

  const isActive = product.active !== false;

  // Helper to determine the process group based on DB group, stepId and name keywords
  const getProcessGroupKey = (o: any): string => {
    const stepId = (o.stepId || o.process?.stepId || "").toUpperCase().replace(/-/g, "_");
    // Mã tự sinh dùng gạch ngang (SAU-IN-THUC-NOI): chuẩn hoá để không bị nhận nhầm là công đoạn In (FB-7H1WJ3)
    const name = (o.name || o.process?.name || "").toLowerCase();
    const dbGroup = (o.group || o.process?.group || "").toUpperCase();

    // 1. Direct stepId markers
    if (stepId.includes("TRUOC_IN") || stepId.includes("PRE_PRESS"))
      return "TRUOC_IN";
    if (stepId.includes("SAU_IN") || stepId.includes("POST_PRESS"))
      return "SAU_IN";
    if (stepId.includes("THANH_PHAM") || stepId.includes("TP"))
      return "THANH_PHAM";
    if (
      stepId.includes("IN") &&
      !stepId.includes("SAU_IN") &&
      !stepId.includes("TRUOC_IN")
    )
      return "IN";

    // 2. Mapping based on DB group
    if (["OFFSET_PRINTER", "FLEXO_PRINTER", "IN", "PRINTING"].includes(dbGroup))
      return "IN";
    if (
      [
        "COATER",
        "SILK_SCREEN",
        "HOT_FOIL",
        "SAU_IN",
        "POST_PRESS",
        "GIA_CONG_SAU_IN",
      ].includes(dbGroup)
    )
      return "SAU_IN";
    if (
      [
        "DIE_CUTTER",
        "FOLDER_GLUER",
        "WINDOW_PATCHER",
        "PACKING",
        "THANH_PHAM",
      ].includes(dbGroup)
    )
      return "THANH_PHAM";
    if (
      [
        "TRUOC_IN",
        "PRE_PRESS",
        "GHEP_MANG",
        "GHEP MANG",
        "GHÉP MÀNG",
        "LAMINATING",
      ].includes(dbGroup)
    )
      return "TRUOC_IN";

    // Special DB groups that overlap (LAMINATOR, PAPER_CUTTER)
    if (dbGroup === "LAMINATOR") {
      if (name.includes("ghép")) return "TRUOC_IN";
      return "SAU_IN"; // Cán màng bóng/mờ/nhung...
    }
    if (dbGroup === "PAPER_CUTTER") {
      if (name.includes("xén") || name.includes("cắt giấy")) return "TRUOC_IN";
      return "THANH_PHAM"; // Cắt thành phẩm...
    }

    // 3. Keyword fallback for group: null or legacy groups
    if (
      name.includes("in offset") ||
      name.includes("in flexo") ||
      name.includes("in lụa") ||
      name.includes("cmyk") ||
      name.startsWith("in ") ||
      name.includes(" màu ")
    ) {
      if (name.includes("uv") || name.includes("phủ")) return "SAU_IN";
      return "IN";
    }
    if (
      name.includes("ghép") ||
      name.includes("metalize") ||
      name.includes("xén") ||
      name.includes("cắt giấy") ||
      name.includes("bát")
    ) {
      return "TRUOC_IN";
    }
    if (
      name.includes("cán") ||
      name.includes("phủ") ||
      name.includes("vecni") ||
      name.includes("uv") ||
      name.includes("ép nhũ") ||
      name.includes("ép kim") ||
      name.includes("nhũ") ||
      name.includes("kim") ||
      name.includes("tráng") ||
      name.includes("vân")
    ) {
      return "SAU_IN";
    }

    return "THANH_PHAM";
  };

  // Helper for rendering standard packaging process groups
  const renderProcessGroup = (
    groupKey: "TRUOC_IN" | "IN" | "SAU_IN" | "THANH_PHAM",
    spanClass = "sm:col-span-2 lg:col-span-3 mt-1",
  ) => {
    const titleLabel = {
      TRUOC_IN: "Trước in",
      IN: "In ấn",
      SAU_IN: "Sau in",
      THANH_PHAM: "Thành phẩm",
    }[groupKey];

    const groupItems = junctionProcesses.filter(
      (pp: any) => getProcessGroupKey(pp) === groupKey,
    );

    return (
      <div
        key={groupKey}
        className={`flex flex-col gap-1 min-w-0 sm:flex-row sm:gap-2 ${spanClass}`}
      >
        <span className="text-slate-700 text-xs shrink-0 sm:w-24 font-medium sm:pt-0.5">
          {titleLabel}
        </span>
        <div className="flex flex-wrap gap-1.5 flex-1 items-start">
          {groupItems.length > 0 ? (
            groupItems.map((pp: any) => (
              <Badge
                key={pp.id}
                variant="outline"
                className="px-2 py-0.5 bg-blue-50/50 text-blue-600 border-blue-200 text-xs font-normal shadow-none break-words whitespace-normal max-w-full"
              >
                {pp.process?.name || "?"}
              </Badge>
            ))
          ) : (
            <span className="text-slate-600 text-xs">—</span>
          )}
        </div>
      </div>
    );
  };

  const isBoxProduct =
    (product.productType?.group || product.productType?.code || "").toLowerCase().includes("hộp") ||
    (product.unit || "").toLowerCase() === "hộp";

  const formattedDimensions =
    product.length && product.width && product.height && Number(product.height) > 0
      ? `${product.length}x${product.width}x${product.height} mm`
      : specsParts.length === 3 && Number(specsParts[2]) > 0
        ? `${specsParts.join("x")} mm`
        : !isBoxProduct && product.length && product.width && Number(product.length) > 0 && Number(product.width) > 0
          ? `${product.length}x${product.width} mm`
          : "—";

  return (
    <StandardDetailLayout
        tabTitle={product.productCode || productId}
      isZIndex={isZIndex}
      breadcrumbs={[{ label: "Sản phẩm", to: "/products" }]}
      onBack={handleBack}
      overrideBack={!!returnUrl}
      title={
        <div className="flex items-center gap-2">
          <span>{product.productCode || productId}</span>
          {product.currentVersion && (
            <VersionHistoryModal
              productId={product.id}
              currentVersion={product.currentVersion}
              trigger={
                <Badge
                  variant="outline"
                  className="bg-primary/5 text-primary border-primary/20 text-xs cursor-pointer hover:bg-primary/10 transition-colors"
                >
                  v{product.currentVersion}
                </Badge>
              }
            />
          )}
        </div>
      }
      entityId={product?.id || productId}
      entityType="product"
      timestamps={{ created: product.createdAt, updated: product.updatedAt }}
      status={
        <InteractiveStatusSwitch
          initialStatus={product.active !== false}
          activeValue={true}
          inactiveValue={false}
          entityName="sản phẩm này"
          onToggle={async (newStatus) => {
            await trpcClient.products.update.mutate({
              id: product.id,
              data: { active: newStatus },
            });
            queryClient.invalidateQueries({ queryKey: ["product", productId] });
          }}
        />
      }
      actions={
        <div className="flex flex-wrap gap-1.5">
          {canCreate && (
            <Button
              variant="outline"
              size="sm"
              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50 font-medium"
              onClick={() =>
                navigate({
                  to: "/products/create",
                  search: { sourceId: productId },
                } as any)
              }
            >
              <Copy size={14} className="mr-1.5" /> Nhân bản
            </Button>
          )}
          {canUpdate && (
            <>
              <Button
                variant="default"
                size="sm"
                onClick={() => {
                  navigate({
                    to: "/products/$productId/edit",
                    params: { productId },
                    search: { returnUrl: returnUrl || window.location.pathname },
                  });
                }}
              >
                <Pencil size={14} className="mr-1.5" /> Sửa
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCostPreviewOpen(true)}
                className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
              >
                <Calculator size={14} className="mr-1.5" />
                Tính giá vốn
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleSyncMaster}
                disabled={isSyncingMaster}
                className="text-amber-700 border-amber-200 hover:bg-amber-50"
                title="Bóc tách tự động và lưu vào bảng Định mức & Quy trình chuẩn của sản phẩm"
              >
                {isSyncingMaster ? (
                  <Loader2 size={14} className="mr-1.5 animate-spin" />
                ) : (
                  <Zap size={14} className="mr-1.5" />
                )}
                Đồng bộ BOM
              </Button>
            </>
          )}
          {canDelete && (
            <Button
              variant="outline"
              size="sm"
              className="text-rose-600 border-rose-200 hover:bg-rose-50"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Xoá
            </Button>
          )}
        </div>
      }
      showContextPanel
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xoá"
        description={`Bạn có chắc muốn xoá "${product.productName}"?`}
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ═══ 1. FLAT INFO ═══ */}
      <div className="mb-4 border border-slate-200 rounded-lg px-4 py-4 bg-white">
        {/* Top Row: Basic Info (3 cols) + Product Image (1 col) */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-5 items-start">
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
            <F label="Mã SP" value={product.productCode} mono hl />
            <F
              label="Nhóm SP"
              value={
                product.productType?.group || product.productType?.code || "—"
              }
            />
            <F
              label="Kích thước"
              value={formattedDimensions}
            />
            <F
              label="Khổ trải"
              value={
                (product.flatLength && product.flatWidth)
                  ? `${product.flatLength}x${product.flatWidth} mm`
                  : (product.specs?.flatLength && product.specs?.flatWidth)
                    ? `${product.specs.flatLength}x${product.specs.flatWidth} mm`
                    : "—"
              }
              hl
            />

            <F
              label="Tên SP"
              value={product.productName}
              className="sm:col-span-2"
            />
            <F label="Đơn vị tính" value={product.unit} />

            <F label="Khổ giấy in" value={product.printFormat || printSizeValue || "—"} />
            <F label="Khổ giấy mua" value={product.cutFormat || purchaseSizeValue || "—"} />
            <F label="Số bát" value={product.outs || outsValue || 1} />

            <F
              label="Quy cách đóng gói"
              value={product.packingSpec || "—"}
              className="sm:col-span-2"
            />
            <F
              label="Số cái / thùng"
              value={product.pcsPerBox ? `${product.pcsPerBox} cái / thùng` : "—"}
            />
            {product.notes && (
              <F label="Ghi chú" value={<span className="whitespace-pre-wrap" data-testid="product-view-notes">{product.notes}</span>} className="sm:col-span-full" />
            )}
            <F
              label="Giá vốn chuẩn"
              value={`${formatMoney(Number(product.costPrice || product.standardCost || 0))} đ`}
            />
            <F
              label="Đơn giá (Catalog)"
              value={`${formatMoney(Number(product.price || 0))} đ`}
              hl
              className="sm:col-span-2"
            />

            <F
              label="Hướng thớ giấy"
              value={
                <div className="flex items-center gap-1.5 flex-wrap" data-testid="product-view-grain-direction">
                  <Badge
                    variant="outline"
                    data-testid="product-view-grain-badge"
                    className={cn(
                      "text-[11px] font-medium",
                      specsObj.grainDirection === "LONG"
                        ? "bg-blue-50 text-blue-700 border-blue-200"
                        : specsObj.grainDirection === "SHORT"
                          ? "bg-indigo-50 text-indigo-700 border-indigo-200"
                          : "bg-slate-50 text-slate-600 border-slate-200"
                    )}
                  >
                    {specsObj.grainDirection === "LONG"
                      ? "↕️ Thớ dọc"
                      : specsObj.grainDirection === "SHORT"
                        ? "↔️ Thớ ngang"
                        : "Tự do"}
                  </Badge>
                  {specsObj.strictGrainDirection && (
                    <Badge
                      variant="outline"
                      data-testid="product-view-strict-grain-badge"
                      className="text-[10px] bg-amber-50 text-amber-800 border-amber-300 font-semibold"
                    >
                      🔒 Khóa chiều xoay
                    </Badge>
                  )}
                </div>
              }
              className="sm:col-span-2"
            />
            <F
              label="Chứng nhận FSC"
              value={
                specsObj.fscCode && specsObj.fscCode !== "NONE" ? (
                  <Badge
                    variant="outline"
                    data-testid="product-view-fsc-badge"
                    className="text-[11px] bg-emerald-50 text-emerald-700 border-emerald-300 font-semibold whitespace-normal text-left break-words max-w-full shrink rounded-md"
                  >
                    🌲 {specsObj.fscCode}
                  </Badge>
                ) : (
                  "—"
                )
              }
            />

            <F
              label="Khách hàng"
              value={
                product.customerId ? (
                  <EntityLink
                    entityType="customer"
                    id={product.customerId}
                    label={product.customer?.name || product.customer?.shortName}
                    search={{ returnUrl: `/products/${productId}` }}
                  />
                ) : (
                  product.customer?.name || product.customer?.shortName || "—"
                )
              }
              className="sm:col-span-3"
            />
          </div>

          {/* RIGHT: Product Image */}
          <div className="lg:col-span-1">
            {product.image ? (
              <ImageLightbox src={getUploadUrl(product.image)} alt={product.productName}>
                <div className="border border-slate-200 rounded-lg overflow-hidden bg-white p-1 shadow-sm cursor-zoom-in hover:border-indigo-300 hover:shadow-md transition-all group">
                  <img
                    src={getUploadUrl(product.image)}
                    alt={product.productName}
                    className="object-cover w-full h-auto max-h-[220px] rounded-md transition-transform duration-200 group-hover:scale-[1.02]"
                  />
                </div>
              </ImageLightbox>
            ) : (
              <div className="border border-slate-200 rounded-lg bg-slate-50 flex items-center justify-center p-6 shadow-sm flex-col gap-2 min-h-[180px]">
                <div className="h-10 w-10 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center mb-1 bg-white">
                  <Package className="h-5 w-5 text-slate-300" />
                </div>
                <span className="text-xs text-slate-600 font-medium">
                  Chưa có hình ảnh
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Full-Width Section Underneath Image: Chất liệu, Trước in, In ấn, Sau in, Thành phẩm */}
        <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
          {/* Chất liệu (4 cột, gộp tên vật tư gọn gàng tránh lặp với bảng Định mức Vật tư bên dưới) */}
          <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:gap-2 lg:col-span-4">
            <span className="text-slate-700 text-xs shrink-0 sm:w-24 font-medium sm:pt-0.5">
              Chất liệu
            </span>
            <div className="flex flex-wrap gap-1.5 flex-1 items-start">
              {product.productMaterials?.length > 0 ? (
                product.productMaterials.map((pm: any) => (
                  <Badge
                    key={pm.materialId}
                    variant="outline"
                    className="px-2 py-0.5 bg-slate-50 text-slate-700 border-slate-200 text-xs font-normal shadow-none break-words whitespace-normal max-w-full"
                  >
                    {cleanBrandName(pm.material?.name || pm.material?.materialId || pm.materialId)}
                  </Badge>
                ))
              ) : product.materialId || product.material?.id ? (
                <Badge
                  variant="outline"
                  className="px-2 py-0.5 bg-slate-50 text-slate-700 border-slate-200 text-xs font-normal shadow-none break-words whitespace-normal max-w-full"
                >
                  {cleanBrandName(product.material?.name || product.material?.materialId || product.materialId)}
                </Badge>
              ) : (
                <span className="text-slate-600 text-xs">—</span>
              )}
            </div>
          </div>

          {/* Hàng: Trước in (2 cột) | In ấn (2 cột) */}
          {renderProcessGroup("TRUOC_IN", "lg:col-span-2")}
          {renderProcessGroup("IN", "lg:col-span-2")}

          {/* Hàng: Sau in (2 cột) | Thành phẩm (2 cột) */}
          {renderProcessGroup("SAU_IN", "lg:col-span-2")}
          {renderProcessGroup("THANH_PHAM", "lg:col-span-2")}

          {/* Các công đoạn khác không thuộc nhóm nào */}
          {(() => {
            const unmapped = junctionProcesses.filter((pp: any) => {
              const key = getProcessGroupKey(pp);
              return !["TRUOC_IN", "IN", "SAU_IN", "THANH_PHAM"].includes(
                key,
              );
            });
            if (unmapped.length === 0) return null;
            return (
              <div className="flex flex-col gap-1 min-w-0 sm:flex-row sm:gap-2 sm:col-span-2 lg:col-span-4">
                <span className="text-slate-700 text-xs shrink-0 sm:w-24 font-medium sm:pt-0.5">
                  Khác
                </span>
                <div className="flex flex-wrap gap-1.5 flex-1 items-start">
                  {unmapped.map((pp: any) => (
                    <Badge
                      key={pp.id}
                      variant="outline"
                      className="px-2 py-0.5 bg-gray-50 text-gray-700 border-gray-200 text-xs font-normal shadow-none break-words whitespace-normal max-w-full"
                    >
                      {pp.process?.name || "?"}
                    </Badge>
                  ))}
                </div>
              </div>
            );
          })()}

          {junctionProcesses.length === 0 && (product.process || textProcesses.length > 0) && (
            <F
              label="Quy trình"
              value={String(
                product.process || textProcesses.join(", "),
              ).replace(/\+/g, ", ")}
              className="sm:col-span-2 lg:col-span-4"
            />
          )}

          {product.tags && (
            <F
              label="Ghi chú"
              value={product.tags}
              className="sm:col-span-2 lg:col-span-4"
            />
          )}
        </div>

        {/* Components for Composite Products */}
        {product.isComposite &&
          product.components &&
          product.components.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-200">
              <h4 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Thành phần Bộ sản phẩm
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.components.map((c: any, idx: number) => (
                  <div
                    key={c.id || idx}
                    className="border border-emerald-100 bg-emerald-50/30 rounded-md p-3"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-sm text-slate-800">
                        {c.componentName}
                      </div>
                      <Badge variant="outline" className="bg-white">
                        SL: {c.quantity} {c.unit}
                      </Badge>
                    </div>
                    <div className="space-y-1.5 mt-2 text-[13px]">
                      {c.bomItems?.length > 0 && (
                        <div className="flex gap-2">
                          <span className="text-slate-500 min-w-[50px]">
                            Vật tư:
                          </span>
                          <span className="text-slate-700">
                            {c.bomItems
                              .map(
                                (b: any) => cleanBrandName(b.material?.name) || b.materialId,
                              )
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      {c.routingSteps?.length > 0 && (
                        <div className="flex gap-2">
                          <span className="text-slate-500 min-w-[50px]">
                            C.Đoạn:
                          </span>
                          <span className="text-slate-700">
                            {c.routingSteps
                              .map((r: any) => r.process?.name || r.processId)
                              .join(", ")}
                          </span>
                        </div>
                      )}
                      {c.specs && (
                        <div className="flex gap-2">
                          <span className="text-slate-500 min-w-[50px]">
                            Mô tả:
                          </span>
                          <span className="text-slate-700">{c.specs}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
      </div>

      {/* ═══ 3. FK TABS: Đơn hàng | Giao hàng ═══ */}
      <div className="border rounded-lg bg-white shadow-sm overflow-hidden">
        <Tabs value={subTab} onValueChange={setSubTab}>
          <TabsList>
            <TabsTrigger
              value="bom"
            >
              <LayersIcon className="h-3.5 w-3.5 mr-1.5" />
              Định mức Vật tư
            </TabsTrigger>
            <TabsTrigger
              value="routing"
            >
              <ListTree className="h-3.5 w-3.5 mr-1.5" />
              Quy trình
            </TabsTrigger>
            <TabsTrigger
              value="cost-breakdown"
            >
              <Calculator className="h-3.5 w-3.5 mr-1.5 text-indigo-600" />
              Bảng chiết giá
            </TabsTrigger>
            <TabsTrigger
              value="orders"
            >
              <Package className="h-3.5 w-3.5 mr-1.5" />
              Đơn hàng
            </TabsTrigger>
            <TabsTrigger
              value="deliveries"
            >
              <Truck className="h-3.5 w-3.5 mr-1.5" />
              Giao hàng
            </TabsTrigger>
            <TabsTrigger
              value="toolings"
            >
              <Cpu className="h-3.5 w-3.5 mr-1.5" />
              Khuôn & Kẽm
            </TabsTrigger>
          </TabsList>

          {/* ── BOM ── */}
          <TabsContent value="bom" className="p-0 m-0">
            <div className="overflow-x-auto">
              <Table data-testid="product-bom-table">
                <TableHeader className="bg-slate-50/80">
                  <TableRow>
                    <TableHead className="w-[50px] text-center">STT</TableHead>
                    <TableHead>Mã VT</TableHead>
                    <TableHead>Tên Vật tư</TableHead>
                    <TableHead className="text-right">Định mức / 1 SP</TableHead>
                    <TableHead>ĐVT</TableHead>
                    <TableHead>Công đoạn</TableHead>
                    <TableHead>Diễn giải định mức</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.productMaterials &&
                  product.productMaterials.length > 0 ? (
                    product.productMaterials.map((pm: any, index: number) => (
                      <TableRow key={pm.id || index}>
                        <TableCell className="text-center text-slate-500">
                          {index + 1}
                        </TableCell>
                        <TableCell className="font-medium text-indigo-600 font-mono">
                          <EntityLink
                            entityType="material"
                            id={pm.material?.id || pm.materialId}
                            code={pm.material?.materialId || pm.material?.code || pm.materialId}
                          />
                        </TableCell>
                        <TableCell>{cleanBrandName(pm.material?.name) || "—"}</TableCell>
                        <TableCell className="text-right font-semibold font-mono text-emerald-700">
                          {formatBomNorm(pm.standardQuantity)}
                        </TableCell>
                        <TableCell>
                          {pm.unit || pm.material?.unit || "—"}
                        </TableCell>
                        <TableCell>
                          {pm.process?.process?.name || pm.process?.name || "—"}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {pm.note || "—"}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="h-24 text-center text-slate-500"
                      >
                        Chưa có dữ liệu định mức vật tư
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ── ROUTING ── */}
          <TabsContent value="routing" className="p-0 m-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-slate-50/80">
                  <TableRow>
                    <TableHead className="w-[80px] text-center">
                      Thứ tự
                    </TableHead>
                    <TableHead>Công đoạn</TableHead>
                    <TableHead>Nhóm</TableHead>
                    <TableHead className="text-right">
                      TG thiết lập
                    </TableHead>
                    <TableHead className="text-right">
                      Tốc độ
                    </TableHead>
                    <TableHead className="text-right">
                      Bù hao chạy
                    </TableHead>
                    <TableHead className="text-right">
                      Bù hao cố định
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {product.productProcesses &&
                  product.productProcesses.length > 0 ? (
                    [...product.productProcesses]
                      .sort(
                        (a: any, b: any) =>
                          (a.sortOrder ?? a.stepOrder ?? 0) - (b.sortOrder ?? b.stepOrder ?? 0),
                      )
                      .map((pp: any, index: number) => (
                        <TableRow key={pp.id || index}>
                          <TableCell className="text-center font-medium">
                            {pp.sortOrder ?? index + 1}
                          </TableCell>
                          <TableCell className="font-medium text-indigo-600">
                            {pp.process?.name || pp.processId}
                          </TableCell>
                          <TableCell>
                            <ProcessGroupBadge process={pp.process} showDetail />
                          </TableCell>
                          <TableCell className="text-right">
                            {pp.setupTimeMinutes
                              ? formatNumber(pp.setupTimeMinutes)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right">
                            {pp.speedPerHour
                              ? formatNumber(pp.speedPerHour)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right text-rose-600">
                            {pp.runWastePercent
                              ? `${pp.runWastePercent}%`
                              : "—"}
                          </TableCell>
                          <TableCell className="text-right text-rose-600">
                            {pp.setupWasteQty
                              ? formatNumber(pp.setupWasteQty)
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        className="h-24 text-center text-slate-500"
                      >
                        Chưa có dữ liệu quy trình
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* ── BẢNG CHIẾT GIÁ ── */}
          <TabsContent value="cost-breakdown" className="p-4 m-0 space-y-5">
            {isLoadingDecomposition ? (
              <div className="flex items-center justify-center p-12 text-slate-500">
                <Loader2 className="h-6 w-6 animate-spin mr-2" /> Đang tải bảng chiết giá...
              </div>
            ) : decompositionData ? (
              <>
                {/* Header Thống kê & Nút đồng bộ */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-slate-50 border rounded-lg">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-slate-900 text-base">
                        Tổng hợp chi phí giá vốn
                      </span>
                      {decompositionData.hasExistingMasterBOM && decompositionData.hasExistingMasterRouting ? (
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                          Định mức chuẩn
                        </Badge>
                      ) : (
                        <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                          <Sparkles className="w-3 h-3 mr-1 text-amber-600" />
                          Tự động tính
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Khổ in: {decompositionData.printFormat || "Chuẩn"} • Khổ mộc: {decompositionData.cutFormat || "Chuẩn"} • Số bát: {decompositionData.outs}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className="text-xs text-slate-500 block">Đơn giá vốn ước tính</span>
                      <span className="text-lg font-bold text-indigo-700">
                        {formatMoney(decompositionData.summary.unitCost)} đ
                      </span>
                      <span className="text-xs text-slate-400"> / {product.unit || "cái"}</span>
                    </div>

                    {(!decompositionData.hasExistingMasterBOM || !decompositionData.hasExistingMasterRouting) && (
                      <Button
                        size="sm"
                        onClick={handleSyncMaster}
                        disabled={isSyncingMaster}
                        className="bg-amber-600 hover:bg-amber-700 text-white shadow-sm"
                      >
                        {isSyncingMaster ? (
                          <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        ) : (
                          <Zap className="w-4 h-4 mr-1.5" />
                        )}
                        Lưu định mức chuẩn
                      </Button>
                    )}
                  </div>
                </div>

                {/* 3 Thẻ Chỉ số chi phí */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="p-3.5 bg-blue-50/60 border border-blue-100 rounded-lg">
                    <span className="text-xs text-blue-700 font-medium block">Chi phí vật tư</span>
                    <span className="text-lg font-bold text-blue-900 mt-1 block">
                      {formatMoney(decompositionData.summary.totalMaterialCost)} đ
                    </span>
                  </div>
                  <div className="p-3.5 bg-indigo-50/60 border border-indigo-100 rounded-lg">
                    <span className="text-xs text-indigo-700 font-medium block">Chi phí công đoạn</span>
                    <span className="text-lg font-bold text-indigo-900 mt-1 block">
                      {formatMoney(decompositionData.summary.totalProcessCost)} đ
                    </span>
                  </div>
                  <div className="p-3.5 bg-purple-50/60 border border-purple-100 rounded-lg">
                    <span className="text-xs text-purple-700 font-medium block">Phí khuôn bản</span>
                    <span className="text-lg font-bold text-purple-900 mt-1 block">
                      {formatMoney(decompositionData.summary.totalFixedCost)} đ
                    </span>
                  </div>
                </div>

                {/* Bảng 1: Vật tư */}
                <div className="border rounded-md overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 font-medium text-xs text-slate-700 border-b flex items-center justify-between">
                    <span>1. Chi phí vật tư</span>
                    <span className="text-slate-500 font-normal">
                      {decompositionData.components[0]?.materials?.length || 0} mục vật tư
                    </span>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <Table>
                      <TableHeader className="bg-slate-50/50">
                        <TableRow>
                          <TableHead className="w-[45px] text-center">STT</TableHead>
                          <TableHead>Tên vật tư</TableHead>
                          <TableHead className="w-[50px]">ĐVT</TableHead>
                          <TableHead className="text-right">SL định mức</TableHead>
                          <TableHead className="text-right">SL bù hao</TableHead>
                          <TableHead className="text-right">SL yêu cầu</TableHead>
                          <TableHead className="text-right">Đơn giá</TableHead>
                          <TableHead className="text-right">Thành tiền</TableHead>
                          <TableHead>Ghi chú</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {decompositionData.components[0]?.materials?.map((m: any, idx: number) => {
                          const totalReq = Number(m.calculatedQuantity || 0);
                          const setupWaste = Number(m.setupWasteQty || 0);
                          const runWaste = Math.round(totalReq * (Number(m.runWastePct || 0) / 100));
                          const totalWaste = setupWaste + runWaste;
                          const netStd = Math.max(0, totalReq - totalWaste);

                          return (
                            <TableRow key={idx}>
                              <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                              <TableCell>
                                <div className="font-medium text-slate-900">{cleanBrandName(m.materialName)}</div>
                                {m.materialCode && (
                                  <div className="text-[10px] font-mono text-slate-500">{m.materialCode}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-slate-600">{m.unit || "TỜ"}</TableCell>
                              <TableCell className="text-right font-mono text-slate-600">
                                {formatNumber(netStd > 0 ? netStd : Math.ceil(m.standardQuantity * (decompositionData.targetQuantity || 1000)))}
                              </TableCell>
                              <TableCell className="text-right font-mono text-amber-600" title={`Setup: ${setupWaste} • Chạy: ${m.runWastePct || 0}%`}>
                                +{formatNumber(totalWaste > 0 ? totalWaste : setupWaste)}
                              </TableCell>
                              <TableCell className="text-right font-mono font-bold text-slate-900">
                                {formatNumber(totalReq)}
                              </TableCell>
                              <TableCell className="text-right font-mono">{formatMoney(m.unitPrice)}</TableCell>
                              <TableCell className="text-right font-mono font-semibold text-blue-700">{formatMoney(m.calculatedCost)}</TableCell>
                              <TableCell className="text-xs text-slate-500 max-w-[200px] break-words whitespace-normal" title={m.note}>{m.note || "—"}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Bảng 2: Công đoạn */}
                <div className="border rounded-md overflow-hidden">
                  <div className="px-4 py-2 bg-slate-50 font-medium text-xs text-slate-700 border-b flex items-center justify-between">
                    <span>2. Chi phí công đoạn</span>
                    <span className="text-slate-500 font-normal">
                      {decompositionData.components[0]?.routings?.length || 0} công đoạn
                    </span>
                  </div>
                  <div className="overflow-x-auto w-full">
                    <Table>
                      <TableHeader className="bg-slate-50/50">
                        <TableRow>
                          <TableHead className="w-[50px] text-center">STT</TableHead>
                          <TableHead>Công đoạn</TableHead>
                          <TableHead>Nhóm</TableHead>
                          <TableHead className="text-right">Phí thiết lập</TableHead>
                          <TableHead className="text-right">Đơn giá</TableHead>
                          <TableHead className="text-right">Bù hao</TableHead>
                          <TableHead className="text-right">Thành tiền</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {decompositionData.components[0]?.routings?.map((r: any, idx: number) => (
                          <TableRow key={idx}>
                            <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                            <TableCell className="font-medium text-slate-900">{r.processName}</TableCell>
                            <TableCell>
                              <ProcessGroupBadge group={r.group} name={r.processName} showDetail size="sm" />
                            </TableCell>
                            <TableCell className="text-right">{formatMoney(r.setupCost)}</TableCell>
                            <TableCell className="text-right">{formatMoney(r.unitCost)}</TableCell>
                            <TableCell className="text-right">{r.runWastePct}%</TableCell>
                            <TableCell className="text-right font-semibold text-indigo-700">{formatMoney(r.calculatedCost)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              <div className="p-8 text-center text-slate-500">
                Không thể bóc tách giá thành cho sản phẩm này
              </div>
            )}
          </TabsContent>

          {/* ── Orders ── */}
          <TabsContent value="orders" className="p-3">
            <ProductOrdersTab productId={product.id} />
          </TabsContent>

          {/* ── Deliveries ── */}
          <TabsContent value="deliveries" className="p-3">
            <ProductDeliveriesTab productId={product.id} />
          </TabsContent>

          {/* ── Toolings (Khuôn & Kẽm) ── */}
          <TabsContent value="toolings" className="p-0 m-0">
            <div className="space-y-6 p-4">
              {/* Bảng 1: Khuôn (Die Cuts / Molds) */}
              <div className="border rounded-md overflow-hidden bg-white">
                <div className="px-4 py-2.5 bg-slate-50 font-medium text-xs text-slate-700 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Boxes className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold text-slate-800">Danh sách khuôn</span>
                  </div>
                  <Badge variant="outline" className="text-xs bg-white">
                    {linkedMolds.length} khuôn
                  </Badge>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-slate-50/80">
                      <TableRow>
                        <TableHead className="w-[50px] text-center">STT</TableHead>
                        <TableHead>Mã khuôn</TableHead>
                        <TableHead>Tên khuôn</TableHead>
                        <TableHead>Loại khuôn</TableHead>
                        <TableHead>Kích thước</TableHead>
                        <TableHead className="text-center">Số bát</TableHead>
                        <TableHead>Trạng thái</TableHead>
                        <TableHead>Ghi chú</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedMolds.length > 0 ? (
                        linkedMolds.map((m: any, idx: number) => (
                          <TableRow key={m.id || idx}>
                            <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                            <TableCell className="font-medium text-indigo-600 font-mono">
                              <EntityLink
                                entityType="mold"
                                id={m.id}
                                code={m.code}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-slate-800">{m.name || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs font-normal">
                                {m.type === "DIE_CUT"
                                  ? "Khuôn bế"
                                  : m.type === "FOIL"
                                    ? "Khuôn ép nhũ"
                                    : m.type === "EMBOSS"
                                      ? "Khuôn thúc nổi"
                                      : m.type || "Khuôn"}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs">{m.size || "—"}</TableCell>
                            <TableCell className="text-center font-mono font-medium">{m.ups || 1}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-xs font-normal",
                                  m.status === "ACTIVE" || m.status === "READY"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : m.status === "DAMAGED" || m.status === "BROKEN"
                                      ? "bg-rose-50 text-rose-700 border-rose-200"
                                      : "bg-slate-50 text-slate-700 border-slate-200"
                                )}
                              >
                                {m.status || "Hoạt động"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-slate-500 max-w-[200px] break-words whitespace-normal" title={m.notes}>
                              {m.notes || (m.isDirect ? "Khuôn tạo trực tiếp cho SP" : "Khuôn liên kết chung")}
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={8} className="h-20 text-center text-slate-500">
                            Chưa có khuôn nào được liên kết với sản phẩm này
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Bảng 2: Kẽm in (Plates) */}
              <div className="border rounded-md overflow-hidden bg-white">
                <div className="px-4 py-2.5 bg-slate-50 font-medium text-xs text-slate-700 border-b flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-indigo-600" />
                    <span className="font-semibold text-slate-800">Danh sách bản kẽm</span>
                  </div>
                  <Badge variant="outline" className="text-xs bg-white">
                    {linkedPlates.length} bản kẽm
                  </Badge>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader className="bg-slate-50/80">
                      <TableRow>
                        <TableHead className="w-[50px] text-center">STT</TableHead>
                        <TableHead>Mã bản kẽm</TableHead>
                        <TableHead>Tên bản kẽm</TableHead>
                        <TableHead>Kích thước</TableHead>
                        <TableHead>Màu in</TableHead>
                        <TableHead>Trạng thái</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedPlates.length > 0 ? (
                        linkedPlates.map((p: any, idx: number) => (
                          <TableRow key={p.id || idx}>
                            <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                            <TableCell className="font-medium text-indigo-600 font-mono">
                              <EntityLink
                                entityType="plate"
                                id={p.id}
                                code={p.plateId || p.id}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-slate-800">{p.name || "—"}</TableCell>
                            <TableCell className="font-mono text-xs">{p.size || "—"}</TableCell>
                            <TableCell className="text-xs">{p.colors || "—"}</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={cn(
                                  "text-xs font-normal",
                                  p.status === "ACTIVE" || p.status === "READY"
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                    : "bg-slate-50 text-slate-700 border-slate-200"
                                )}
                              >
                                {p.status || "Hoạt động"}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} className="h-20 text-center text-slate-500">
                            Chưa có bản kẽm nào được liên kết với sản phẩm này
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <ProductCostPreviewModal
        open={costPreviewOpen}
        onOpenChange={setCostPreviewOpen}
        product={product}
      />
      <ProductSyncResultModal
        open={Boolean(syncResult)}
        onOpenChange={(open) => {
          if (!open) setSyncResult(null);
        }}
        result={syncResult}
        productUnit={product.unit || "SP"}
        onViewBom={() => setSubTab("bom")}
      />
    </StandardDetailLayout>
  );
}
